// Selection-Store fuer Canvas-Objekte. Pro Board-View lokal — wird in spaeteren Phasen
// ggf. mit Awareness (S4.6) gespiegelt, ist aber selbst nicht synchronisiert.

import { useCallback, useState } from "react";

export interface UseSelectionApi {
  selected: ReadonlySet<string>;
  isSelected: (id: string) => boolean;
  select: (id: string, additive?: boolean) => void;
  selectMany: (ids: Iterable<string>, additive?: boolean) => void;
  clear: () => void;
  remove: (id: string) => void;
}

export function useSelection(): UseSelectionApi {
  const [selected, setSelected] = useState<Set<string>>(() => new Set());

  const select = useCallback((id: string, additive?: boolean): void => {
    setSelected((prev) => {
      if (additive) {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      }
      if (prev.size === 1 && prev.has(id)) return prev;
      return new Set([id]);
    });
  }, []);

  const selectMany = useCallback((ids: Iterable<string>, additive?: boolean): void => {
    setSelected((prev) => {
      const incoming = Array.from(ids);
      if (incoming.length === 0) {
        return additive ? prev : new Set();
      }
      const base = additive ? new Set(prev) : new Set<string>();
      for (const id of incoming) base.add(id);
      return base;
    });
  }, []);

  const clear = useCallback(() => setSelected(new Set()), []);
  const remove = useCallback((id: string) => {
    setSelected((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const isSelected = useCallback((id: string): boolean => selected.has(id), [selected]);

  return { selected, isSelected, select, selectMany, clear, remove };
}
